/**
 * Live-updating balance + accrual display. Shows current balance, effective
 * hourly earn rate (with capacity comparison), accumulating unclaimed
 * tokens, security multiplier, and brand awareness with its own ticker.
 */
import { useEffect, useRef, useState } from "react";
import type { TycoonState } from "../api/tycoon.api";
import { AnimatePresence, motion } from "framer-motion";

interface Props {
  balance: bigint;
  tycoonState: TycoonState | null;
  onClaim: () => void;
}

function formatTokens(n: bigint): string {
  const s = n.toString();
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function tickerAccrual(rate: bigint, lastClaimMs: number): bigint {
  if (rate === 0n) return 0n;
  const elapsedHours = Math.min((Date.now() - lastClaimMs) / 3_600_000, 8);
  if (elapsedHours <= 0) return 0n;
  const milliHours = BigInt(Math.round(elapsedHours * 1000));
  return (rate * milliHours) / 1000n;
}

export function BalanceTicker({ balance, tycoonState, onClaim }: Props) {
  const [accrued, setAccrued] = useState<bigint>(0n);
  const [brandTicker, setBrandTicker] = useState<bigint>(0n);

  // Track balance deltas so we can pop a "+N" floaty over the balance after
  // a claim — confirms the token credit visually even though the balance
  // also jumps. Distinct from the in-office Floaty component.
  const prevBalance = useRef<bigint>(balance);
  const [floaters, setFloaters] = useState<Array<{ id: number; amount: bigint }>>([]);
  const floatId = useRef(0);

  useEffect(() => {
    const delta = balance - prevBalance.current;
    if (delta > 0n && prevBalance.current > 0n) {
      const id = ++floatId.current;
      setFloaters((arr) => [...arr, { id, amount: delta }]);
      window.setTimeout(() => {
        setFloaters((arr) => arr.filter((f) => f.id !== id));
      }, 1500);
    }
    prevBalance.current = balance;
  }, [balance]);

  useEffect(() => {
    if (!tycoonState) return;
    const lastClaim = new Date(tycoonState.exchange.lastPassiveClaimAt).getTime();
    const tokenRate = BigInt(tycoonState.exchange.hourlyTokenRate);
    const tokenCap = BigInt(tycoonState.exchange.passiveClaimCap);
    const brandStored = BigInt(tycoonState.exchange.brandAwarenessStored);
    const brandRate = BigInt(tycoonState.exchange.brandAwarenessRate);

    const update = () => {
      // Clamp the local accrual to the server's per-claim cap so the
      // ticker doesn't promise more than the server will actually pay.
      const raw = tickerAccrual(tokenRate, lastClaim);
      setAccrued(raw > tokenCap ? tokenCap : raw);
      setBrandTicker(brandStored + tickerAccrual(brandRate, lastClaim));
    };
    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [tycoonState]);

  const effectiveRate = tycoonState ? BigInt(tycoonState.exchange.hourlyTokenRate) : 0n;
  const capacityRate = tycoonState ? BigInt(tycoonState.exchange.hourlyCapacity) : 0n;
  const securityPct = tycoonState?.security.effectivePct ?? 0;
  const securityNet = tycoonState?.security.net ?? 0;
  const supply = tycoonState?.security.supply ?? 0;
  const demand = tycoonState?.security.demand ?? 0;
  const claimable = accrued > 0n;
  const tokenCap = tycoonState ? BigInt(tycoonState.exchange.passiveClaimCap) : 0n;
  const atCap = tokenCap > 0n && accrued >= tokenCap;
  const brandRate = tycoonState ? BigInt(tycoonState.exchange.brandAwarenessRate) : 0n;
  const securityColor =
    securityPct >= 90 ? "good" : securityPct >= 50 ? "warn" : "bad";

  return (
    <div className="balance-ticker">
      <div className="balance-row">
        <span className="balance-label">Balance</span>
        <span className="balance-value-wrap">
          <motion.span
            className="balance-value"
            key={balance.toString()}
            initial={{ scale: 1.12, color: "#ffd23f" }}
            animate={{ scale: 1, color: "#eaecef" }}
            transition={{ duration: 0.45, ease: "easeOut" }}
          >
            {formatTokens(balance)} <span className="balance-unit">tokens</span>
          </motion.span>
          {/* Float-up "+N" toasts on credit. Stacks if multiple deltas land. */}
          <AnimatePresence>
            {floaters.map((f) => (
              <motion.span
                key={f.id}
                className="balance-floater"
                initial={{ opacity: 0, y: 0, scale: 0.85 }}
                animate={{ opacity: 1, y: -28, scale: 1 }}
                exit={{ opacity: 0, y: -42 }}
                transition={{ duration: 1.4, ease: "easeOut" }}
              >
                +{formatTokens(f.amount)}
              </motion.span>
            ))}
          </AnimatePresence>
        </span>
      </div>

      <div className="ticker-row">
        <div className="rate-block">
          <span className="muted">Earning</span>
          <strong>{formatTokens(effectiveRate)}/hr</strong>
          {capacityRate > effectiveRate && capacityRate > 0n && (
            <span className="capacity-loss" title="Capacity lost to low security">
              of {formatTokens(capacityRate)} capacity
            </span>
          )}
        </div>
        <button
          className={`claim-btn ${claimable ? "active" : ""} ${atCap ? "at-cap" : ""}`}
          onClick={onClaim}
          disabled={!claimable}
          type="button"
          title={
            atCap
              ? `Claim cap reached (${formatTokens(tokenCap)}). Claim and keep playing to keep earning.`
              : undefined
          }
        >
          {claimable ? (
            <>
              +{formatTokens(accrued)}
              {atCap && <span className="cap-pill">MAX</span>}
            </>
          ) : (
            "Nothing to claim"
          )}
        </button>
      </div>

      <div className="meter-row">
        <div className={`meter security ${securityColor}`} title={`Supply ${supply} − Demand ${demand} = ${securityNet}`}>
          <div className="meter-label">
            <span>🛡 Security</span>
            <strong>{securityPct}%</strong>
          </div>
          <div className="meter-bar">
            <motion.div
              className="meter-fill"
              animate={{ width: `${securityPct}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <div className="meter-foot">
            <span>{supply} supply</span>
            <span className="muted">−</span>
            <span>{demand} demand</span>
          </div>
        </div>

        <div className="meter brand" title="Spend on opening new city branches">
          <div className="meter-label">
            <span>📣 Brand</span>
            <strong>{formatTokens(brandTicker)}</strong>
          </div>
          <div className="meter-foot">
            <span>+{formatTokens(brandRate)}/hr</span>
          </div>
        </div>
      </div>
    </div>
  );
}
